<script setup lang="ts">
/**
 * UiKitCheckbox — чекбокс HeroUI UI-kit. Figma: 657-2392.
 * Цвета только из токенов (классы hui-*).
 */
import { BG, BORDER, TEXT_FG, RING, type HuiColor, type HuiSize } from './palette'

const props = withDefaults(
  defineProps<{
    color?: HuiColor
    size?: HuiSize
    value?: string | number
    disabled?: boolean
    invalid?: boolean
    indeterminate?: boolean
  }>(),
  { color: 'primary', size: 'md', value: undefined, disabled: false, invalid: false, indeterminate: false },
)

/** boolean (одиночный) или массив значений (группа) */
const model = defineModel<boolean | (string | number)[]>({ default: false })

defineOptions({ name: 'UiKitCheckbox' })

const checked = computed(() => {
  if (Array.isArray(model.value)) return props.value !== undefined && model.value.includes(props.value)
  return !!model.value
})

function toggle() {
  if (props.disabled) return
  if (Array.isArray(model.value)) {
    if (props.value === undefined) return
    const set = new Set(model.value)
    if (set.has(props.value)) set.delete(props.value)
    else set.add(props.value)
    model.value = [...set]
  } else {
    model.value = !model.value
  }
}

const BOX_SIZE: Record<HuiSize, string> = { sm: 'w-4 h-4 rounded-[4px]', md: 'w-5 h-5 rounded-md', lg: 'w-6 h-6 rounded-[7px]' }
const ICON_SIZE: Record<HuiSize, string> = { sm: 'w-3 h-3', md: 'w-3.5 h-3.5', lg: 'w-4 h-4' }
const TEXT_SIZE: Record<HuiSize, string> = { sm: 'text-sm', md: 'text-base', lg: 'text-lg' }

const isOn = computed(() => checked.value || props.indeterminate)
const boxClass = computed(() => [
  'relative inline-flex items-center justify-center shrink-0 box-border border-2 transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-hui-background',
  BOX_SIZE[props.size],
  RING[props.invalid ? 'danger' : props.color],
  isOn.value
    ? `${BG[props.invalid ? 'danger' : props.color]} ${BORDER[props.invalid ? 'danger' : props.color]} ${TEXT_FG[props.invalid ? 'danger' : props.color]}`
    : `bg-transparent ${props.invalid ? 'border-hui-danger' : 'border-hui-default-300'}`,
])
</script>

<template>
  <label
    class="relative inline-flex items-center gap-2 select-none"
    :class="disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'"
  >
    <input
      type="checkbox"
      class="sr-only"
      :checked="checked"
      :disabled="disabled"
      @change="toggle"
    />
    <span :class="boxClass" :tabindex="disabled ? -1 : 0" @keydown.space.prevent="toggle">
      <svg
        v-if="indeterminate"
        :class="ICON_SIZE[size]"
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        stroke-width="3"
      >
        <path d="M5 10h10" stroke-linecap="round" />
      </svg>
      <svg
        v-else
        :class="[ICON_SIZE[size], 'transition-transform duration-150', checked ? 'scale-100' : 'scale-0']"
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        stroke-width="3"
      >
        <path d="M4 10.5l4 4 8-9" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </span>
    <span v-if="$slots.default" :class="[TEXT_SIZE[size], 'text-hui-foreground leading-tight']">
      <slot />
    </span>
  </label>
</template>
